/** Botones que abren la ayuda: el de la barra de arriba (la pantalla actual) y el de cada ventana compleja. */
import { CircleHelp } from 'lucide-react';
import { useState } from 'react';
import { useAuth } from '@/lib/auth.tsx';
import { TEMA_GENERAL, useAyuda, useAyudaOpcional } from './contexto.tsx';
import { temaPorId } from './registro.ts';

/** Ayuda ya abierta en esta pantalla (por usuario, en este navegador): el botón deja de marcarse como nuevo. */
const claveVista = (usuario: string, tema: string) => `mg-ayuda-vista:${usuario}:${tema}`;
function vista(usuario: string, tema: string): boolean {
  try {
    return localStorage.getItem(claveVista(usuario, tema)) === '1';
  } catch {
    return true;
  }
}
function marcarVista(usuario: string, tema: string) {
  try {
    localStorage.setItem(claveVista(usuario, tema), '1');
  } catch {
    // Sin almacenamiento del navegador la marca no se guarda: no pasa nada
  }
}

/** Botón de la barra de arriba: abre la ayuda de lo que se ve (F1 hace lo mismo). Un punto avisa si nunca la abriste aquí. */
export function BotonAyuda() {
  const { abrir, temaPantalla } = useAyuda();
  const { usuario } = useAuth();
  const tema = temaPantalla ?? TEMA_GENERAL;
  const [, refrescar] = useState(0);
  const nueva = !!usuario && !vista(usuario.id, tema);
  return (
    <button
      type="button"
      className="relative inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm font-medium text-mg-verde-oscuro hover:bg-accent"
      onClick={() => {
        if (usuario) marcarVista(usuario.id, tema);
        refrescar((n) => n + 1);
        abrir();
      }}
      title={`Ayuda de esta pantalla: ${temaPorId(tema)?.titulo ?? 'cómo usar el sistema'} (F1)`}
      data-testid="btn-ayuda"
    >
      <CircleHelp className="h-4 w-4" />
      <span className="hidden md:inline">Ayuda</span>
      {nueva ? <span className="absolute right-1 top-1 h-2 w-2 rounded-full bg-mg-ambar" data-testid="btn-ayuda-nueva" /> : null}
    </button>
  );
}

/** En las pantallas sin cabecera (impresión): un botón flotante abajo a la derecha que no sale en el papel. */
export function AyudaFlotante({ tema }: { tema: string }) {
  const ayuda = useAyudaOpcional();
  if (!ayuda) return null;
  const { abrir } = ayuda;
  return (
    <button type="button" className="no-print fixed bottom-4 right-4 z-40 inline-flex items-center gap-1.5 rounded-full border bg-card px-3 py-2 text-sm font-medium text-mg-verde-oscuro shadow-md hover:bg-accent" onClick={() => abrir(tema)} data-testid="btn-ayuda-flotante">
      <CircleHelp className="h-4 w-4" /> Ayuda
    </button>
  );
}

/** El «?» del encabezado de una ventana: abre la ayuda de esa ventana. Va junto al título. */
export function AyudaVentana({ tema, className = '' }: { tema: string; className?: string }) {
  const ayuda = useAyudaOpcional();
  if (!ayuda) return null;
  const { abrir } = ayuda;
  return (
    <button type="button" className={`inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full align-middle text-muted-foreground hover:bg-accent hover:text-mg-verde ${className}`} onClick={() => abrir(tema)} aria-label="Ayuda de esta ventana" title="Ayuda de esta ventana" data-testid={`ayuda-${tema}`}>
      <CircleHelp className="h-4 w-4" />
    </button>
  );
}
